import { nothing, TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { Unsubscribe } from 'redux';
import { PassValidationCompStatus } from '../../common/constants/api-constants';
import store from '../../store/store';
import { StoreState } from '../../store/store.interface';
import { FormBaseComponent } from '../base-component/forms/form-base-component';
import '../password-validation/password-validation';
import { updatePassValidationCompStatus } from '../user-profile';
import { selectPasswordValidationStatus } from '../user-profile/user-profile.selectors';
import { isLMSFlow } from '../../common/utils/my-account-utils';

/*
  MyAccountFormWrapper should only be inherited by My Account form/fields 
  where you want to show readonly Mode or edit Mode for form. 
  When the password is validated (using PasswordValidation component) 
  then we update the redux store with status "success", we listen to this 
  status update in this component so that it know it should render the form 
  in readonly mode or edit mode. 

  so, when status is "default" then show readonly Mode
  else show edit mode.

  -----------------------------------------------------------------------------------------

  The `connect` function is a utility for integrating PWA Helper with Redux by subscribing to store changes.

  Usage example:
  ```
  class Parent extends connect(store)(LitElement) {}
  class Child extends connect(store)(Parent) {}
  ```

  When using the `connect` function with inheritance, a potential issue arises. Connect function
  doesn't invoke `super.stateChanged`,so parent's `stateChanged` method won't be invoked.

  To address this, a manual implementation of the `stateChanged` method is added to the parent class:
  ```
  class Parent extends connect(store)(LitElement) {
    stateChanged(_state) {
        // Additional logic specific to the parent class
        super.stateChanged(_state); // Ensure the parent's stateChanged method is called
    }
  }
  ```
  This ensures that even if a child class overrides the `stateChanged` method, the parent's logic will still be executed.
*/
@customElement('mhecomm-my-account-form-wrapper')
export class MyAccountFormWrapper extends FormBaseComponent {

  @state()
  protected compState: PassValidationCompStatus = PassValidationCompStatus.default;

  /*
    in case we have mulitple component on single page then if password is validated then
    all the component will on the page will show the edit mode instead on the one you have 
    clicked. so, to solve that problem we set this field to true (when the edit button is clicked), 
    and once password is validated then we check both the status and validateMode value.
  */
  @state()
  protected validateMode = false;

  @state()
  protected editMode = false;

  @state()
  protected isLMSFlow = isLMSFlow();

  protected _storeUnsubscribe!: Unsubscribe;

  connectedCallback() {
    super.connectedCallback();
    this._storeUnsubscribe = store.subscribe(
      () => this._stateChanged(store.getState() as StoreState)
    );
  }

  _stateChanged(state: StoreState): void {
    this.compState = selectPasswordValidationStatus(state);
    this.bindListnersForInputChangeEvent();
    if (this.compState === PassValidationCompStatus.default) {
      this.validateMode = false;
    } 
    
    if (this.compState === PassValidationCompStatus.succeeded) {
      this.editMode = true;
    } else {
      this.editMode = false;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._storeUnsubscribe();
  }

  renderComp() {
    if (this.isEditMode()) {
      return this.renderEditMode();
    } else {
      return this.renderReadonlyMode();
    }
  }

  isEditMode() {
    return this.validateMode && this.compState === PassValidationCompStatus.succeeded;
  }
  
  isDefault() {
    return this.compState === PassValidationCompStatus.default;
  }

  updateComponentStatus(status: PassValidationCompStatus) {
    this.validateMode = true;
    store.dispatch(updatePassValidationCompStatus(status));
  }

  renderEditMode(): TemplateResult | typeof nothing {
    return nothing;
  }

  renderReadonlyMode(): TemplateResult | typeof nothing {
    return nothing;
  }

  protected createErrorElement(element: HTMLInputElement) {
    return `
      <div class="alert alert-danger py-2 fs-6 invalid-feedback d-block" role="alert" aria-live="assertive" id="${element.id}-field-error">
        <uxt-icon icon="exclamation-triangle" class="me-1"></uxt-icon><span class="visually-hidden">Error&nbsp;</span>${element.validationMessage}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-my-account-form-wrapper': MyAccountFormWrapper;
  }
}
